<template>
  <ModalCard
    v-if="isOpen"
    :title="`Merge ${pluralize(sampleMetaModel.label || 'sample', 2)}`"
    :handle-close="() => $emit('update:is-open', false)"
    width="80%"
    style="overflow: auto"
  >
    <template #body>
      <div style="height: 100%">
        <div style="height: 100%">
          <BaseButton
            size="small"
            variant="primary"
            :outlined="!groupByKey"
            :icon="[
              'fa-solid',
              groupByKey ? 'fa-object-ungroup' : 'fa-object-group',
            ]"
            :label="groupByKey ? 'Ungroup' : 'Group by name'"
            style="float: right; padding-left: 1rem"
            @click="
              () => {
                groupByKey = groupByKey === 'name' ? '' : 'name'
                keptSampleIds = sampleGroups.map((s) => '')
              }
            "
          />
          <div>
            <div style="padding-bottom: 0.5rem">
              <p>
                You are about to merge multiple
                {{ pluralize(sampleMetaModel.label || 'sample', 2) }}
                together.
              </p>
              <p>
                This feature should be used when duplicate have been created
                while they actually are the same and unique entity.
              </p>
              <p style="margin-top: 0.5rem">
                <a
                  v-if="showMergeDetails === true"
                  @click="
                    () => {
                      showMergeDetails = false
                    }
                  "
                >
                  - details
                </a>
                <a
                  v-else
                  @click="
                    () => {
                      showMergeDetails = true
                    }
                  "
                >
                  + details
                </a>
              </p>
            </div>
            <div
              v-if="showMergeDetails"
              style="display: flex; flex-wrap: wrap; padding-bottom: 1rem"
            >
              <div
                style="
                  flex-basis: 50%;
                  flex-grow: 1;
                  flex-shrink: 1;
                  min-width: 400px;
                "
              >
                Sample merging operation key aspects:
                <ol style="list-style: decimal; padding: 0 3rem">
                  <li style="padding: 0.2rem 0">
                    A single sample is
                    <b>kept</b>
                    while the other ones are
                    <span class="has-text-danger"><b>deleted</b></span>
                  </li>
                  <li style="padding: 0.2rem 0">
                    Relationships from & to removed samples are transferred to
                    the kept sample
                  </li>
                  <li style="padding: 0.2rem 0">
                    Samples can be automatically grouped into different groups
                    using the
                    <b>Group By Name</b>
                    button. In this case, one sample is kept per group. When a
                    sample does not group with others, it will be kept
                    untouched.
                  </li>
                </ol>
              </div>
              <div style="flex-shrink: 0">
                <img
                  src="https://s3.embl.de/gbcs-public/stocks_ui/assets/stocks-sample-merge-overview.jpg"
                  alt="Sample merge overview"
                  style="min-width: 500px"
                />
              </div>
            </div>
            <div class="notification-container">
              <div class="notification is-info" style="padding: 0.3rem 0">
                <span class="icon">
                  <i class="fa-solid fa-exclamation-triangle" />
                </span>
                <span style="width: 100%">
                  Double check your settings. If mistakes are made, there is
                  <b>no possible rollback</b>
                  to a previous state.
                </span>
              </div>
            </div>
          </div>
          <div style="text-align: right; padding-top: 1rem">
            <p>
              <span v-if="groupByKey">
                Samples have been grouped by
                <b>{{ groupByKey }}</b>
                . Pick one sample to keep for each group before validating.
              </span>
            </p>
            <p v-if="ignoredSamples.length > 0" class="help has-text-warning">
              <b>
                {{ ignoredSamples.length }} samples have been ignored because
                they don't group with other samples using the grouping key
                <em>{{ groupByKey }}</em>
              </b>
            </p>
          </div>
          <div style="overflow: auto">
            <div
              v-for="(s, index) in sampleGroups"
              :key="`samplegroup${index}-${s.value}`"
            >
              <hr v-show="sampleGroups.length > 1 && index > 0" />
              <MergeSampleGroup
                :columns="displayColumns"
                :samples="s.samples"
                :permissions-by-id="permissionsById"
                :group-name="`Group ${index + 1}`"
                @keep-sample="
                  ({ keptId, discardedIds }) => {
                    keptSampleIds[index] = keptId
                    discardedSampleIds[index] = discardedIds
                  }
                "
              />
              <p
                v-if="keptSampleIds[index]"
                class="has-text-success"
                style="text-align: center"
              >
                Merging
                {{ s.samples.length }}
                {{ pluralize(sampleMetaModel.label || 'sample', 2) }} into
                <b>
                  {{ keptSamples[index].name.value.name }}
                </b>
                . The {{ discardedSampleIds[index].length }} other
                {{
                  pluralize(
                    sampleMetaModel.label || 'sample',
                    discardedSampleIds.length,
                  )
                }}
                will be deleted in the process.
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div style="width: 100%">
        <div style="margin-left: auto">
          <BaseButton
            style="padding-right: 0.5rem"
            size="small"
            :outlined="true"
            label="Cancel"
            :disabled="isMerging"
            @click="$emit('update:is-open', false)"
          />
          <BaseButton
            size="small"
            variant="primary"
            :outlined="true"
            :icon="['fa-solid', 'fa-compress-arrows-alt']"
            label="Merge samples"
            :loading="isMerging"
            :disabled="
              !hasChosenAllSamplesToKeep ||
              sampleGroups.length === 0 ||
              isMerging
            "
            @click="handleMergeSamples"
          />
        </div>
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { get as _get } from 'lodash-es'
import pluralize from 'pluralize'

import { put } from '@/api'

import MergeSampleGroup from '@/components/listings/MergeSampleGroup.vue'

export default {
  name: 'ListMergeSamples',
  emits: ['update:is-open', 'merged-samples'],
  components: { MergeSampleGroup },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    samples: {
      type: Array,
      default: () => [],
    },
    sampleMetaModel: {
      type: Object,
    },
    permissionsById: {
      type: Object,
      default: () => {},
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      groupByKey: '',
      keptSampleIds: [],
      discardedSampleIds: [],
      showMergeDetails: true,
      isMerging: false,
    }
  },
  computed: {
    displayColumns() {
      const prioritizeColumnIds = ['name', 'created']
      const prioritizeColumns = this.columns.filter((c) =>
        prioritizeColumnIds.includes(c.id),
      )
      const otherVisibleCols = this.columns.filter(
        (c) => !prioritizeColumnIds.includes(c.id),
      )
      return [...prioritizeColumns, ...otherVisibleCols]
    },
    sampleGroupsByGroupKey() {
      // let sampleGroupsList = []
      const oth = {}
      this.samples.reduce((sByName, s) => {
        /* If we allow grouping with other props, here the code has to be
         * updated. But reliably being able to compare attributes means
         * extra work. Should write a FieldCompare mixin/lib */

        const groupVal = this.groupByKey
          ? _get(s, `${this.groupByKey}.value.name`, '')
          : 'default' // no grouping key, only one group
        if (groupVal) {
          if (groupVal in sByName) {
            sByName[groupVal].push(s)
          } else {
            sByName[groupVal] = [s]
          }
        }
        return sByName
      }, oth)
      return oth
    },
    baseSampleGroups() {
      return Object.keys(this.sampleGroupsByGroupKey).map((sName) => {
        return {
          value: sName,
          samples: this.sampleGroupsByGroupKey[sName],
        }
      })
    },
    sampleGroups() {
      return this.baseSampleGroups.filter((g) => g.samples.length > 1)
    },
    ignoredSamples() {
      return this.baseSampleGroups
        .filter((g) => g.samples.length < 2)
        .reduce((samps, g) => {
          samps.push(...g.samples)
          return samps
        }, [])
    },
    keptSamples() {
      return this.keptSampleIds.map((id, index) => {
        const sampleGroup = this.sampleGroups[index].samples || []
        return id ? sampleGroup.find((s) => s.id.value === id) : {}
      })
    },
    totalSamplesNumber() {
      return (
        this.discardedSampleIds.reduce(
          (total, dSample) => total + dSample.length,
          0,
        ) + this.keptSampleIds.length
      )
    },
    hasChosenAllSamplesToKeep() {
      return (
        this.keptSampleIds.filter((id) => id !== '').length >=
        this.sampleGroups.length
      )
    },
  },
  watch: {
    isOpen: function (isOpen) {
      if (isOpen === false) this.reset()
    },
  },
  methods: {
    pluralize,
    reset() {
      this.keptSampleIds = []
      this.discardedSampleIds = []
      this.groupByKey = ''
    },
    handleMergeSamples() {
      this.isMerging = true
      const mergePayload = this.sampleGroups
        .filter(
          (group, index) =>
            this.keptSampleIds[index] !== '' &&
            this.discardedSampleIds[index].length,
        )
        .map((group, index) => {
          return [this.keptSampleIds[index], ...this.discardedSampleIds[index]]
        })
      return put('stocks/samples/merge/', { data: mergePayload })
        .then((response) => {
          if (response.status === 202) {
            this.$emit('merged-samples')
            //might want to reset stuff here
            this.$store.commit('notify', {
              type: 'success',
              message: `Succesfully merged ${this.totalSamplesNumber} samples into ${this.keptSampleIds.length}.`,
            })
          }
        })
        .catch((e) => {
          const errorMessage =
            e.response?.data?.message ||
            `Something went wrong merging samples (${e})`
          this.$store.commit('notify', {
            type: 'error',
            message: errorMessage,
          })
        })
        .finally(() => {
          this.isMerging = false
        })
    },
  },
}
</script>
